import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AutumnLeaves } from "@/components/OurStory.jsx";

/**
 * SecretLetter
 * -----------------------------------------------------------
 * Only on the `the-secret-letter` branch: a vintage handwritten letter
 * with autumn leaves falling. The button at the end takes her to the
 * live wedding invitation (INVITE_URL), not this branch's copy.
 */

// Each entry is one paragraph of the letter, revealed one after another
const LETTER = [
  "So finally, here we are. From 19 October 2025 to 19 October 2026: one whole year, and now we are about to tie the knot.",
  "You were always meant to be mine, and I was always meant to be yours. Kyunki Raj toh Sona ka hi hai… I love you, I love you the most.",
  "Having you by my side is everything I ever wanted. I can’t even describe what happened to me, or how it happened. Arrggg, I just love you the most! Aur kya bolun, pata nahi…",
  "I can’t wait anymore. The most patient person in my family is losing all his patience. Your biwi paglu!",
  "From our first message, to meeting for the first time, to holding hands, and now holding our hearts together. You are my home.",
  "Do you know you make me happy? Do you know you make me smile? And you make me laugh, every single day.",
  "I promise I will never stop trying, never stop putting in the effort, to keep you happy.",
  "I am really emotional and teary while writing this, and while building all of this for us. I love you, you are my love.",
  "So here is a little surprise for you. Look how far we have come, from that first day to today. It has all led us here…",
];

const INK = "#5A2A1A"; // warm sepia ink (never black)

// Photos taped into the letter, shown after the paragraph with that index
const PHOTOS = {
  0: { src: "/letter/0.jpg", alt: "Sonakshi and Shubham at the café, the day they first met", tilt: 2.5 },
  2: { src: "/letter/1.jpg", alt: "Sonakshi and Shubham sitting together", tilt: -3, wide: true },
  7: { src: "/letter/2.jpg", alt: "Sonakshi and Shubham dancing", tilt: 2.5 },
};


// The real, always-up-to-date invitation (production site)
const INVITE_URL = "https://sonakaraj.com/";
const FALLBACK_URL = "https://sona-ka-raj.pages.dev/";

// Ping the .com first; if it does not answer within 2.5s (DNS not ready,
// flaky network) open the pages.dev copy so the invitation always loads.
function openInvite(e) {
  e.preventDefault();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 2500);
  fetch(INVITE_URL, { mode: "no-cors", cache: "no-store", signal: ctrl.signal })
    .then(() => INVITE_URL)
    .catch(() => FALLBACK_URL)
    .then((url) => {
      clearTimeout(timer);
      window.location.href = url;
    });
}

const reveal = (i) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: 0.6 + i * 0.35, ease: [0.22, 1, 0.36, 1] },
});

export default function SecretLetter() {
  // The cover needs one tap: that tap also unlocks the song (browsers block autoplay)
  const [opened, setOpened] = useState(false);
  return (
    <section
      className="relative min-h-full w-full overflow-y-auto overflow-x-hidden flex justify-center px-4 py-10 sm:py-16"
      style={{
        background:
          "radial-gradient(ellipse at 50% 20%, #F6E9CF 0%, #E9D3AC 55%, #C9A574 100%)",
      }}
    >
      <AutumnLeaves count={22} />
      <VinylToggle />

      {!opened && (
        <button
          type="button"
          onClick={() => setOpened(true)}
          className="fixed inset-0 z-30 flex flex-col items-center justify-center text-center px-6"
          style={{ color: INK }}
          aria-label="Open your letter"
        >
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-script text-4xl sm:text-5xl"
          >
            A letter for you, Sona
          </motion.span>
          <motion.span
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: [1, 1.06, 1], opacity: 1 }}
            transition={{ scale: { duration: 2.2, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 1, delay: 0.3 } }}
            className="relative mt-8 w-24 h-24 rounded-full flex items-center justify-center shadow-[0_10px_24px_rgba(58,10,24,0.35)]"
            style={{ background: "radial-gradient(circle at 30% 30%, #A8264F 0%, #8A0F36 55%, #5C0822 100%)" }}
          >
            <img src="/brand/logo.png" alt="" className="w-[64%] h-auto pointer-events-none" draggable="false" />
            <span className="absolute inset-[9%] rounded-full border border-dashed border-gold-light/50" />
          </motion.span>
          <span className="mt-6 font-letter text-2xl opacity-80">Tap the seal to open</span>
          <span className="mt-1 font-body text-[10px] uppercase tracking-chip opacity-60">Sound on</span>
        </button>
      )}

      {opened && (
      <motion.article
        initial={{ opacity: 0, y: 30, rotate: -0.6 }}
        animate={{ opacity: 1, y: 0, rotate: -0.6 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-xl rounded-sm px-7 py-10 sm:px-12 sm:py-14 shadow-[0_30px_60px_-20px_rgba(90,42,26,0.45)]"
        style={{
          color: INK,
          // aged paper: warm base, darker burnt edges, faint ruled lines
          background:
            "radial-gradient(ellipse at center, rgba(255,250,236,0) 55%, rgba(176,122,62,0.28) 100%)," +
            "repeating-linear-gradient(180deg, transparent 0 33px, rgba(140,96,52,0.10) 33px 34px)," +
            "linear-gradient(170deg, #FBF3E1 0%, #F3E4C4 60%, #EBD6AE 100%)",
          border: "1px solid rgba(140,96,52,0.35)",
        }}
      >
        <div className="text-right font-letter text-xl opacity-80">19 October 2026</div>

        <motion.h1 {...reveal(0)} className="mt-4 font-script text-4xl sm:text-5xl leading-tight">
          Hey baby, mera baby, mera cutu, meri Sona 😘
        </motion.h1>

        <div className="mt-6 space-y-4 font-letter text-[22px] sm:text-2xl leading-snug">
          {LETTER.map((p, i) => (
            <div key={i}>
              <motion.p {...reveal(i + 1)}>{p}</motion.p>
              {PHOTOS[i] && <Polaroid {...PHOTOS[i]} delay={0.6 + (i + 1.5) * 0.35} />}
            </div>
          ))}
        </div>

        <motion.div {...reveal(LETTER.length + 1)} className="mt-10 flex flex-col items-center">
          <a
            href={INVITE_URL}
            onClick={openInvite}
            className="px-8 py-3.5 rounded-full border border-[#8A4B2A] text-[#7A3A1E] font-body text-[11px] uppercase tracking-chip font-semibold bg-[rgba(255,248,232,0.6)] hover:bg-[#7A3A1E] hover:text-[#FBF3E1] transition-colors"
          >
            Open your surprise
          </a>
        </motion.div>

        <motion.div {...reveal(LETTER.length + 2)} className="mt-10 text-right">
          <p className="font-script text-3xl text-center mb-8">And every day, I fall in love with you a little bit more…</p>
          <div className="font-letter text-2xl">Forever yours,</div>
          <div className="font-script text-4xl mt-1">Shubh <span className="text-[#B8174A] text-2xl align-middle">♥</span></div>
          <div className="mt-4 text-center font-letter text-2xl opacity-80">#SonaKaRaj</div>
        </motion.div>

        {/* wax seal with our logo in the corner */}
        <div
          className="absolute -bottom-7 left-8 w-16 h-16 rounded-full flex items-center justify-center shadow-[0_6px_14px_rgba(58,10,24,0.35)]"
          style={{ background: "radial-gradient(circle at 30% 30%, #A8264F 0%, #8A0F36 55%, #5C0822 100%)" }}
          aria-hidden="true"
        >
          <img src="/brand/logo.png" alt="" className="w-[64%] h-auto pointer-events-none" draggable="false" />
          <span className="absolute inset-[9%] rounded-full border border-dashed border-gold-light/50" />
        </div>
      </motion.article>
      )}

      {/* a few leaves drifting in front of the letter too (no taps blocked) */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <AutumnLeaves count={9} />
      </div>
    </section>
  );
}

/* ----------- Letter music: a small vinyl record that spins while playing ----------- */
const LETTER_SONG = "/audio/letter.mp3";

function VinylToggle() {
  const audioRef = useRef(null);
  const userPausedRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = new Audio(LETTER_SONG);
    audio.loop = true;
    audio.volume = 0.55;
    audio.preload = "auto";
    audioRef.current = audio;
    let disposed = false;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    // Browsers block sound until she interacts: try now, else start on her first tap
    const events = ["pointerdown", "keydown", "touchstart"];
    const removeUnlock = () => events.forEach((ev) => document.removeEventListener(ev, unlock, true));
    function unlock(e) {
      if (disposed || userPausedRef.current) return removeUnlock();
      if (e.target?.closest?.("[data-vinyl]")) return; // the disc handles its own tap
      audio.play().then(removeUnlock).catch(() => {});
    }
    audio.play().catch(() => {
      if (!disposed) events.forEach((ev) => document.addEventListener(ev, unlock, true));
    });

    return () => {
      disposed = true;
      removeUnlock();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.pause();
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      userPausedRef.current = true;
      audio.pause();
    } else {
      userPausedRef.current = false;
      audio.play().catch(() => {});
    }
  };

  return (
    <button
      type="button"
      data-vinyl
      onClick={toggle}
      aria-pressed={playing}
      aria-label={playing ? "Pause our song" : "Play our song"}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 w-14 h-14 rounded-full shadow-[0_8px_20px_rgba(90,42,26,0.45)]"
    >
      <span
        className="block w-full h-full rounded-full"
        style={{
          // vinyl grooves + rani label in the middle
          background:
            "radial-gradient(circle, #B8174A 0 17%, #E8CC7E 17% 19%, transparent 19%)," +
            "repeating-radial-gradient(circle, #3A2418 0 1.5px, #4A2E1F 1.5px 3px)",
          animation: "vinyl-spin 3.2s linear infinite",
          animationPlayState: playing ? "running" : "paused",
        }}
      >
        {/* centre hole + a soft shine so the spin is visible */}
        <span className="absolute inset-0 rounded-full" style={{ background: "conic-gradient(from 0deg, rgba(255,240,210,0.18), transparent 25%, rgba(255,240,210,0.12) 50%, transparent 75%)" }} />
        <span className="absolute left-1/2 top-1/2 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FBF3E1]" />
      </span>
      {!playing && (
        <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#FBF3E1] text-[#7A3A1E] text-[10px] flex items-center justify-center shadow">♪</span>
      )}
      <style>{`@keyframes vinyl-spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
}

/* ----------- A polaroid print taped onto the letter ----------- */
function Polaroid({ src, alt, tilt = 0, wide = false, delay = 0 }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 14, rotate: tilt }}
      animate={{ opacity: 1, y: 0, rotate: tilt }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`relative mx-auto my-8 bg-[#FFFBF2] p-2.5 pb-8 shadow-[0_14px_28px_-10px_rgba(90,42,26,0.45)] ${wide ? "w-[92%]" : "w-[68%] sm:w-[58%]"}`}
    >
      {/* washi tape strip */}
      <span
        className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 rotate-[-4deg] opacity-80"
        style={{ background: "repeating-linear-gradient(45deg, rgba(232,160,191,0.75) 0 6px, rgba(244,200,214,0.75) 6px 12px)" }}
        aria-hidden="true"
      />
      <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
    </motion.figure>
  );
}
